import { ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import MobileHeader from "./MobileHeader";
import MobileBottomTabs from "./MobileBottomTabs";
import ContextualHelpPanel from "@/components/ContextualHelpPanel";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import EnvironmentBanner from "@/components/EnvironmentBanner";

const MOBILE_FAQ_MAP: { pattern: string; module: string; page?: string }[] = [
  { pattern: "/app/dashboard", module: "dashboard", page: "overview" },
  { pattern: "/app/wallets", module: "wallets", page: "list" },
  { pattern: "/app/exchange", module: "wallets", page: "exchange" },
  { pattern: "/app/staking/subscribe", module: "staking", page: "subscribe" },
  { pattern: "/app/staking/positions", module: "staking", page: "positions" },
  { pattern: "/app/staking", module: "staking", page: "list" },
  { pattern: "/app/notes-market/sell", module: "notes_market", page: "sell" },
  { pattern: "/app/notes-market/history", module: "notes_market", page: "history" },
  { pattern: "/app/notes-market", module: "notes_market", page: "list" },
  { pattern: "/app/token-sale", module: "token_sale", page: "list" },
  { pattern: "/app/kyc", module: "kyc", page: "submit" },
  { pattern: "/app/security", module: "security", page: "overview" },
  { pattern: "/app/support", module: "support", page: "tickets" },
  { pattern: "/app/referrals", module: "referrals", page: "overview" },
];

/** Routes that render their own full-bleed terminal UI */
const TERMINAL_PREFIXES = [
  "/app/spot/",
  "/app/futures/",
  "/app/binary/",
];

/** Routes that own the full viewport (own header + pinned composer/action bar) */
const FULL_VIEWPORT_PATTERNS = [/^\/app\/p2p\/orders\/[^/]+$/];

function isTerminalRoute(pathname: string) {
  return (
    TERMINAL_PREFIXES.some((p) => pathname.startsWith(p)) ||
    FULL_VIEWPORT_PATTERNS.some((re) => re.test(pathname))
  );
}


function resolveMobileFaqContext(pathname: string) {
  for (const entry of MOBILE_FAQ_MAP) {
    if (pathname === entry.pattern || pathname.startsWith(entry.pattern + "/")) {
      return { moduleKey: entry.module, pageKey: entry.page };
    }
  }
  return null;
}

interface MobileAppShellProps {
  headerActions?: ReactNode;
}

export default function MobileAppShell({ headerActions }: MobileAppShellProps) {
  const location = useLocation();
  const faqContext = resolveMobileFaqContext(location.pathname);
  const isTerminal = isTerminalRoute(location.pathname);

  // Terminal routes get full-bleed layout (no header, no padding, no bottom tabs)
  if (isTerminal) {
    return (
      <div className="h-[100dvh] bg-background flex flex-col overflow-hidden">
        <Outlet />
      </div>
    );
  }

  return (
    <div
      className="mobile-shell min-h-screen bg-background flex flex-col"
      data-shell="mobile"
      style={{ paddingTop: "var(--env-banner-height, 0px)" }}
    >
      <EnvironmentBanner />
      <ImpersonationBanner />
      <MobileHeader actions={headerActions} />
      <main className="flex-1 px-4 py-4 pb-[calc(9.75rem+env(safe-area-inset-bottom))] content-glow-bg relative z-10">
        <Outlet />
        {faqContext && (
          <ContextualHelpPanel
            moduleKey={faqContext.moduleKey}
            pageKey={faqContext.pageKey}
          />
        )}
      </main>
      <MobileBottomTabs />
    </div>
  );
}
